import { useAppTranslation } from "@/utils";
import { GuaranteeType } from "@payping/api/app/api/bnplConsumerTypes";
import type { PlanInfo } from "@payping/api/app/api/bnplConsumerTypes";
import { Button, Flex, Grid, Tag, Typography } from "@payping/web-components";
import { FC } from "react";
import { clsx } from "clsx";
import { CuToman } from "@payping/icons";
import { formatLocalizedNumber } from "@payping/utilities-shared/number";

type PlanType = {
  plan: PlanInfo | undefined;
  onConfirm: () => void;
};

const SelectedPlan: FC<PlanType> = ({ plan, onConfirm }) => {
  const { t } = useAppTranslation();
  const { xs, md, xl, xxl } = Grid.useBreakpoint();
  const desktop = xl || xxl;

  const handleShowGuaranteeType = (type: GuaranteeType) => {
    if (type === GuaranteeType.Cheque) return t("common.guaranteeTypes.cheque");
    if (type === GuaranteeType.None) return t("common.guaranteeTypes.none");
    if (type === GuaranteeType.Salary) return t("common.guaranteeTypes.salary");

    return t("common.guaranteeTypes.promissory");
  };
  return (
    <Flex
      vertical
      gap={"large"}
      className={clsx([
        "tw:relative tw:mx-auto tw:h-full tw:w-full",
        {
          "tw:max-w-full": xs,
          "tw:max-w-120": md,
          "tw:max-w-xl": desktop,
        },
      ])}
    >
      <Flex vertical className="tw:w-full tw:rounded-md tw:border tw:border-solid tw:border-color-border-secondary">
        <Flex
          vertical
          className="tw:rounded-t-md tw:border-0 tw:border-b tw:border-solid tw:border-color-border-secondary tw:bg-color-bg-layout tw:p-3!"
        >
          <Flex align="flex-start" justify="space-between" className="tw:w-full">
            <Flex>
              <Typography.Text className="tw:text-xl! tw:font-semibold!">
                {formatLocalizedNumber({ value: plan?.eachInstallmentPayment ?? 0 })}
              </Typography.Text>
              <CuToman width={24} height={24} />
              <Typography.Text className="tw:text-md!">/ {t("common.monthly")} </Typography.Text>
            </Flex>
            <Tag>{handleShowGuaranteeType(plan?.guaranteeType ?? 0)}</Tag>
          </Flex>
          <Typography.Text type="secondary" className="tw:mt-2! tw:text-sm!">
            {plan?.installmentsCount} {t("common.month")}
          </Typography.Text>
        </Flex>
        <Flex className="tw:p-3!" vertical gap={"middle"} align="flex-start" justify="flex-start">
          <Flex align="flex-start" justify="space-between" className="tw:w-full">
            <Typography.Text type="secondary" className="tw:text-md!">
              {t("pay.operationCost")}
            </Typography.Text>
            <Flex>
              <Typography.Text className="tw:text-md!">
                {formatLocalizedNumber({ value: plan?.consumerOperationCost ?? 0 })}
              </Typography.Text>
              <CuToman width={24} height={24} />
            </Flex>
          </Flex>
          <Flex
            align="flex-start"
            justify="space-between"
            className="tw:w-full tw:border-0 tw:border-b tw:border-dashed tw:border-color-border-secondary tw:pb-4!"
          >
            <Typography.Text type="secondary" className="tw:text-md!">
              {t("pay.prePayment")}
            </Typography.Text>
            <Flex>
              <Typography.Text className="tw:text-md!">
                {formatLocalizedNumber({ value: plan?.prepayment ?? 0 })}
              </Typography.Text>
              <CuToman width={24} height={24} />
            </Flex>
          </Flex>
          <Flex align="flex-start" justify="space-between" className="tw:w-full">
            <Typography.Text type="secondary" className="tw:text-md!">
              {t("pay.finalPayment")}
            </Typography.Text>
            <Flex>
              <Typography.Text className="tw:text-md!">
                {formatLocalizedNumber({ value: plan?.totalFinalPayment ?? 0 })}
              </Typography.Text>
              <CuToman width={24} height={24} />
            </Flex>
          </Flex>
        </Flex>
      </Flex>

      <Flex
        vertical
        className="tw:w-full tw:rounded-md tw:border tw:border-solid tw:border-color-border-secondary tw:p-3!"
      >
        <Typography.Title level={5}>{t("pay.lateFee")}</Typography.Title>
        <Typography.Text>
          {t("pay.lateFeeDescription", {
            value: plan?.delayPenaltydailycharge,
            day: plan?.delayPenaltyFreeInDays,
            currency: t("common.tomans"),
          })}
        </Typography.Text>
      </Flex>

      <Flex
        vertical
        className="tw:w-full tw:rounded-md tw:border tw:border-solid tw:border-color-border-secondary tw:p-3!"
      >
        <Typography.Title level={5}>{t("pay.operationCost")}</Typography.Title>
        <Typography.Text> {t("pay.operationCostDescription")} </Typography.Text>
      </Flex>

      <Flex className="tw:sticky tw:bottom-0 tw:mt-4!">
        <Button type="primary" block onClick={onConfirm}>
          {t("common.confirmAndContinue")}
        </Button>
      </Flex>
    </Flex>
  );
};

export default SelectedPlan;
